import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbAlertCircle, TbChevronDown, TbChevronRight, TbDownload, TbPresentation } from "solid-icons/tb";
import { createMemo, createResource, createSignal, Match, Show, Switch } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { ProcessingSpinner } from "../ui";
import { sanitizeForFilename } from "../util/download";
import { PresentationDeckViewer } from "./PresentationDeckViewer";
import { type Presentation, type PresentationData, presentationToHTML, type Theme } from "./presentationHtml";

interface PresentationPreviewCardProps {
  block: StreamingBlock;
}

/**
 * Inline chat-thread preview for presentation blocks. Hosts with a deck viewer
 * open it via `openContentBlock` (bluesky → side panel); hosts with
 * `caps.inlineMedia` (chief) expand the deck in place — the slide JSON is in
 * the block, so the deck renders as a sandboxed srcdoc iframe with slide
 * image/chart artifacts resolved through `resolveArtifactUrls`.
 */
export function PresentationPreviewCard(props: PresentationPreviewCardProps) {
  const rt = useChatRuntime();

  const data = createMemo<PresentationData | null>(() => {
    if (props.block.finalContent?.type === "text" && props.block.finalContent.text) {
      const parsed = safeParseBlockJson(props.block.finalContent.text) as PresentationData | null;
      if (parsed?.type === "presentation") return parsed;
    }
    const content = getBlockContent(props.block.blockId);
    if (content) {
      const parsed = safeParseBlockJson(content) as PresentationData | null;
      if (parsed?.type === "presentation") return parsed;
    }
    return null;
  });

  const isStreaming = () => !props.block.isComplete;

  const title = createMemo(() => {
    return data()?.metadata?.title || data()?.presentation?.title || rt.t("entity.presentation");
  });

  const subtitle = createMemo(() => {
    const slideCount = data()?.metadata?.slideCount || data()?.presentation?.slides?.length;
    if (slideCount) return `${rt.t("entity.presentation")} · ${slideCount} slides`;
    return rt.t("entity.presentation");
  });

  const inline = () => Boolean(rt.caps.inlineMedia);
  const canOpen = () => Boolean(rt.actions.openContentBlock);
  const [expanded, setExpanded] = createSignal(false);

  const handleClick = () => {
    if (inline()) {
      setExpanded((v) => !v);
    } else {
      rt.actions.openContentBlock?.(props.block.blockId);
    }
  };

  // Resolve slide artifact references lazily on first expand (a copy — the
  // memo's object stays untouched), then hand the deck to the shared viewer.
  const [deck] = createResource(
    () => (expanded() && data()?.presentation ? data() : undefined),
    async (d) => {
      const presentation = structuredClone(d.presentation) as Presentation;
      const resolve = rt.actions.resolveArtifactUrls;
      if (resolve) {
        const artifactIds = presentation.slides.flatMap((slide) => {
          const ids: string[] = [];
          if (slide.image?.artifactId && !slide.image.url) ids.push(slide.image.artifactId);
          if (slide.chart && !slide.chart.url) {
            const id = slide.chart.pngArtifactId || slide.chart.htmlArtifactId;
            if (id) ids.push(id);
          }
          return ids;
        });
        if (artifactIds.length > 0) {
          const resolved = await resolve(artifactIds);
          const urlMap = new Map<string, string>();
          for (const r of resolved) if (r.url) urlMap.set(r.artifactId, r.url);
          for (const slide of presentation.slides) {
            if (slide.image?.artifactId && !slide.image.url) {
              const u = urlMap.get(slide.image.artifactId);
              if (u) slide.image.url = u;
            }
            if (slide.chart && !slide.chart.url) {
              const id = slide.chart.pngArtifactId || slide.chart.htmlArtifactId;
              const u = id ? urlMap.get(id) : undefined;
              if (u) slide.chart.url = u;
            }
          }
        }
      }
      return presentation;
    }
  );

  return (
    <div data-renderer="presentation-renderer">
      <Show
        when={data()?.presentation}
        fallback={
          <Show when={isStreaming()}>
            <div class="flex items-center gap-3 py-2 text-sm text-secondary-text">
              <ProcessingSpinner class="size-4" />
              <span>{rt.t("common.generating_presentation")}</span>
            </div>
          </Show>
        }
      >
        <div class="overflow-hidden rounded-lg border border-primary-border/50">
          <button
            type="button"
            onClick={handleClick}
            disabled={!inline() && !canOpen()}
            class="flex w-full items-center gap-3 p-3 text-left transition-colors enabled:cursor-pointer hover:enabled:bg-canvas-hover"
          >
            <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-inset">
              <TbPresentation class="size-5 text-secondary-text" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium text-primary-text">{title()}</div>
              <div class="mt-0.5 text-xs text-muted-text">{subtitle()}</div>
            </div>
            <Show
              when={inline()}
              fallback={
                <Show when={canOpen()}>
                  <TbChevronRight class="size-4 shrink-0 text-muted-text" />
                </Show>
              }
            >
              <TbChevronDown
                class="size-4 shrink-0 text-muted-text transition-transform"
                classList={{ "-rotate-90": !expanded() }}
              />
            </Show>
          </button>

          <Show when={inline() && expanded()}>
            <Show
              when={deck.state === "ready" && deck()}
              fallback={
                <div class="flex items-center justify-center border-t border-primary-border/50 bg-inset p-8">
                  <Show
                    when={!deck.error}
                    fallback={<span class="text-sm text-secondary-text">{rt.t("chat_failed")}</span>}
                  >
                    <ProcessingSpinner class="size-6" />
                  </Show>
                </div>
              }
            >
              {(presentation) => (
                <div class="border-t border-primary-border/50">
                  <PresentationDeckViewer
                    presentation={presentation()}
                    initialTheme={data()?.metadata?.theme}
                    isStreaming={isStreaming()}
                    framed={false}
                    actions={(ctx) => (
                      <Show when={rt.actions.exportPresentationPdf}>
                        <PresentationPdfButton presentation={presentation()} theme={ctx.theme} title={title()} />
                      </Show>
                    )}
                  />
                </div>
              )}
            </Show>
          </Show>
        </div>
      </Show>
    </div>
  );
}

/**
 * Deck-toolbar button that renders the presentation to a PDF and saves it via
 * the host's `exportPresentationPdf` seam — the same html-to-pdf export the web
 * app runs, so the file matches. Reads the deck's live theme at click time so
 * the PDF reflects whatever theme the user is viewing.
 */
function PresentationPdfButton(props: { presentation: Presentation; theme: () => Theme; title: string }) {
  const rt = useChatRuntime();
  const [downloading, setDownloading] = createSignal(false);
  const [errored, setErrored] = createSignal(false);

  const handleDownload = async () => {
    const exportPdf = rt.actions.exportPresentationPdf;
    if (!exportPdf || downloading()) return;
    rt.actions.trackFeature?.({ feature: "chat.presentation_export", via: "button" });
    setErrored(false);
    setDownloading(true);
    try {
      const html = presentationToHTML(props.presentation, props.theme());
      await exportPdf({
        html,
        title: props.title,
        filename: `${sanitizeForFilename(props.title).slice(0, 80) || "presentation"}.pdf`,
      });
    } catch {
      setErrored(true);
    } finally {
      setDownloading(false);
    }
  };

  const label = () => (errored() ? rt.t("common.failed") : rt.t("tooltip_download"));

  return (
    <button
      type="button"
      onClick={() => void handleDownload()}
      disabled={downloading()}
      aria-label={label()}
      title={label()}
      class="flex items-center gap-1.5 rounded-lg border border-muted-border bg-tile px-3 py-2 text-sm transition-colors hover:bg-tile-hover disabled:opacity-50"
    >
      <Switch fallback={<TbDownload size={16} />}>
        <Match when={downloading()}>
          <ProcessingSpinner class="size-4" />
        </Match>
        <Match when={errored()}>
          <TbAlertCircle class="size-4 text-red-500 dark:text-red-400" />
        </Match>
      </Switch>
      <span>{rt.t("tooltip_download")}</span>
    </button>
  );
}
